/**
 * Top-Level Navigation
 * Show main menu on hover of SHOP (POC - other items will have their own menus later)
 */

class TopLevelNavigation {
  constructor() {
    this.topLevelItems = document.querySelectorAll('[data-top-level-item]');
    // Get the specific menu items wrapper inside nav[data-nav]
    this.menuWrapper = document.querySelector('nav[data-nav] .menu__items[data-takes-space]');
    this.currentHoveredItem = null;
    this.menuJustOpened = false;
    this.hoverDisclosures = null;
    this.allMenuItems = null;
    this.menuItemsByTopLevel = {}; // Store which menu items belong to which top-level item
    this.isMenuOpen = false; // Track menu open state for scroll lock
    this.overlay = null; // Overlay element
    this.isThirdTierOpen = false; // Track if any third-tier dropdown is open
    this.hoverTimeout = null; // Timeout for hover delay
    this.hoverDelay = 400; // Hover delay in milliseconds
    this.resizeObserver = null; // Observer for wrapping detection
    this.init();
  }

  init() {
    if (!this.topLevelItems.length || !this.menuWrapper) {
      return;
    }

    // Create overlay element
    this.createOverlay();

    // Cache all menu items
    this.allMenuItems = this.menuWrapper.querySelectorAll('[data-nav-item]');
    this.hoverDisclosures = this.menuWrapper.querySelectorAll('hover-disclosure');

    // Group menu items by their parent top-level item
    // We'll use data attributes to mark which top-level item each menu item belongs to
    this.groupMenuItems();

    // Set up hover listeners for each top-level item
    this.topLevelItems.forEach((item, index) => {
      const itemNumber = index + 1;

      // First tap opens the menu, blocks ghost clicks
      item.addEventListener('touchstart', (e) => {
        const itemsToShow = this.menuItemsByTopLevel[index + 1] || [];
        // No menu to show
        if (itemsToShow.length === 0) return; 

        // If this items menu is already visible, let the tap navigate
        if (item.classList.contains('top-level-nav__item--menu-open')) return;

        // First tap, open the menu and prevent the touch from clicking through
        e.preventDefault();
        this.currentHoveredItem = index + 1;
        this.showMenu(index + 1);
      }, {passive: false});

      item.addEventListener('mouseenter', () => {
        // Ignore synthesised mouseenter from touch interactions
        if (typeof window.__megaMenuIsRecentTouch === 'function' && window.__megaMenuIsRecentTouch()) return;

        // Clear any existing hover timeout
        if (this.hoverTimeout) {
          clearTimeout(this.hoverTimeout);
        }

        // Set a delay before showing the menu
        this.hoverTimeout = setTimeout(() => {
          // Check if this menu has items before setting as current
          const itemsToShow = this.menuItemsByTopLevel[itemNumber] || [];

          // Always track which item we're hovering
          this.currentHoveredItem = itemNumber;

          if (itemsToShow.length === 0) {
            // No items - hide menu and remove active classes

            // Remove active class from all items
            this.topLevelItems.forEach(item => {
              item.classList.remove('top-level-nav__item--menu-open');
            });

            // Fully close the menu
            if (this.menuWrapper) {
              // Fully hide the menu wrapper
              this.menuWrapper.style.display = 'none';
              this.menuWrapper.style.opacity = '0';
              this.menuWrapper.style.visibility = 'hidden';
              this.menuWrapper.style.pointerEvents = 'none';

              // Remove the default menu spacing to eliminate white gap
              document.documentElement.style.setProperty('--default-menu-spacing', '0px');

              // Hide all menu items
              this.allMenuItems.forEach(item => {
                item.style.display = 'none';
              });

              // Close all open dropdowns
              this.hoverDisclosures.forEach(disclosure => {
                if (disclosure.classList.contains('is-visible')) {
                  disclosure.classList.remove('is-visible');
                  disclosure.setAttribute('aria-expanded', 'false');
                  const dropdown = disclosure.querySelector('.header__dropdown');
                  if (dropdown) {
                    dropdown.classList.remove('is-visible');
                  }
                }
              });

              // Reset third-tier state
              this.isThirdTierOpen = false;
              this.updateOverlay();
              this.ensureScrollUnlocked();
            }
          } else {
            // Has items - show menu normally
            this.showMenu(itemNumber);
          }
        }, this.hoverDelay);
      });

      item.addEventListener('mouseleave', () => {
        // Ignore synthesized mouseleave from touch interactions
        if (typeof window.__megaMenuIsRecentTouch === 'function' && window.__megaMenuIsRecentTouch()) return;

        if (this.hoverTimeout) {
          clearTimeout(this.hoverTimeout);
          this.hoverTimeout = null;
        }
        if (this.currentHoveredItem === itemNumber) {
          this.currentHoveredItem = null;
          this.hideMenu();
        }
      });
    });

    // Keep menu visible when hovering over it
    this.menuWrapper.addEventListener('mouseenter', () => {
      // Ignore synthesised mouse events from touch
      if (typeof window.__megaMenuIsRecentTouch === 'function' && window.__megaMenuIsRecentTouch()) return;
      if (this.currentHoveredItem) {
        this.showMenu(this.currentHoveredItem);
      }
    });

    this.menuWrapper.addEventListener('mouseleave', () => {
      // Ignore synthesised mouse events from touch
      if (typeof window.__megaMenuIsRecentTouch === 'function' && window.__megaMenuIsRecentTouch()) return;
      this.hideMenu();
    });

    // Show SHOP menu by default on page load (desktop only)
    if (window.innerWidth >= 1186) {
      this.showMenu(1); // SHOP menu
      this.currentHoveredItem = 1;
    }

    // Set up listeners for third-tier dropdowns (only for SHOP menu)
    this.setupThirdTierListeners();

    // Set up wrapping detection
    this.setupWrappingDetection();
  }

  createOverlay() {
    // Create overlay element if it doesn't exist
    if (!this.overlay) {
      this.overlay = document.createElement('div');
      this.overlay.className = 'menu-overlay';
      document.body.appendChild(this.overlay);

      // Add click + touch handlers to close menu when tapping overlay
      this.overlay.addEventListener('click', () => {
        this.hideMenu();
      });
      this.overlay.addEventListener('touchstart', (e) => {
        e.preventDefault();
        e.stopPropagation();

        // Close all open hover-disclosures directly (touch guard blocks mouseleave)
        if (this.hoverDisclosures) {
          this.hoverDisclosures.forEach(disclosure => {
            if (typeof disclosure.hideDisclosure === 'function' && disclosure.classList.contains('is-visible')) {
              disclosure.hideDisclosure();
            }
          });
        }

        this.hideMenu();

      }, {passive: false});
    }
  }

  groupMenuItems() {
    // Read the menu group from the data attribute set by the Liquid template
    this.allMenuItems.forEach((item) => {
      const topLevelGroup = parseInt(item.getAttribute('data-top-level-group')) || 1;

      if (!this.menuItemsByTopLevel[topLevelGroup]) {
        this.menuItemsByTopLevel[topLevelGroup] = [];
      }
      this.menuItemsByTopLevel[topLevelGroup].push(item);
    });

    // Determine if each group is 2-tier or 3-tier
    // Group 1 (SHOP): 3-tier with full mega menu and dropdowns
    // Groups 2-4 (EXPLORE, OUTFITS, PRELOVED): 2-tier with simple flat links (no dropdowns)
    this.menuTiersByTopLevel = {};
    Object.keys(this.menuItemsByTopLevel).forEach(groupNum => {
      // Only group 1 (SHOP) uses 3-tier mega menu
      // All others use 2-tier simple dropdown
      this.menuTiersByTopLevel[groupNum] = (groupNum === '1') ? 3 : 2;
    });
  }

  setupThirdTierListeners() {
    // Only set up listeners for SHOP menu (group 1) hover-disclosure elements
    const shopHoverDisclosures = Array.from(this.hoverDisclosures).filter(
      disclosure => parseInt(disclosure.getAttribute('data-top-level-group')) === 1
    );

    shopHoverDisclosures.forEach(disclosure => {
      // Track when third-tier dropdown opens
      disclosure.addEventListener('mouseenter', () => {
        this.isThirdTierOpen = true;
        this.updateOverlay();
      });

      // Track when third-tier dropdown closes
      disclosure.addEventListener('mouseleave', () => {
        // Check if any other third-tier dropdowns are open
        const anyThirdTierOpen = shopHoverDisclosures.some(d =>
          d.classList.contains('is-visible') && d !== disclosure
        );
        this.isThirdTierOpen = anyThirdTierOpen;
        this.updateOverlay();
      });

      // Use MutationObserver to track when is-visible class changes
      const observer = new MutationObserver(() => {
        const anyThirdTierOpen = shopHoverDisclosures.some(d =>
          d.classList.contains('is-visible')
        );
        this.isThirdTierOpen = anyThirdTierOpen;
        this.updateOverlay();
      });

      observer.observe(disclosure, {
        attributes: true,
        attributeFilter: ['class']
      });
    });
  }

  updateOverlay() {
    if (!this.overlay) return;

    // Show overlay when:
    // 1. A 2-tier menu is active (EXPLORE/OUTFITS/PRELOVED), OR
    // 2. A third-tier dropdown is open in SHOP
    // The overlay intercepts taps so they don't pass through to the
    // page content. The ghost-click shield (__megaMenuAddClickShield)
    // handles the synthesised click that follows a touch-close even after the overlay is removed.
    const shouldShowOverlay = this.isMenuOpen &&
      (this.currentHoveredItem !== 1 || this.isThirdTierOpen);

    if (shouldShowOverlay) {
      this.overlay.classList.add('is-visible');
    } else {
      this.overlay.classList.remove('is-visible');
      this.ensureScrollUnlocked();
    }
  }

  showMenu(topLevelNumber) {
    if (this.menuWrapper) {
      // Check if there are any items for this top-level menu FIRST
      const itemsToShow = this.menuItemsByTopLevel[topLevelNumber] || [];

      // If this group is already the active menu and the wrapper is visible, skip the full re-initialisation.
      const alreadyActive =
        parseInt(this.menuWrapper.getAttribute('data-active-menu')) === topLevelNumber &&
        this.menuWrapper.style.opacity === '1';
      if (alreadyActive && itemsToShow.length > 0) {
        return;
      }

      // Don't show menu if there are no items for this top-level item
      if (itemsToShow.length === 0) {
        // Close any previously open menu
        this.hideMenu();
        return;
      }

      // Mark that menu just opened
      this.menuJustOpened = true;

      // Add active class to the current top-level item to persist hover effect
      this.topLevelItems.forEach((item, index) => {
        const itemNumber = index + 1;
        if (itemNumber === topLevelNumber) {
          item.classList.add('top-level-nav__item--menu-open');
        } else {
          item.classList.remove('top-level-nav__item--menu-open');
        }
      });

      // Determine if this is a 2-tier or 3-tier menu
      const isTwoTier = this.menuTiersByTopLevel[topLevelNumber] === 2;
      this.menuWrapper.setAttribute('data-two-tier', isTwoTier);
      this.menuWrapper.setAttribute('data-active-menu', topLevelNumber);

      // For 3-tier menus, close all open dropdowns before showing menu
      // For 2-tier menus, we'll force them all open instead
      if (!isTwoTier) {
        this.hoverDisclosures.forEach(disclosure => {
          if (disclosure.classList.contains('is-visible')) {
            disclosure.classList.remove('is-visible');
            disclosure.setAttribute('aria-expanded', 'false');
            const dropdown = disclosure.querySelector('.header__dropdown');
            if (dropdown) {
              dropdown.classList.remove('is-visible');
            }
          }
          // Temporarily disable pointer events
          disclosure.style.pointerEvents = 'none';
        });
      }

      // Hide all menu items first
      this.allMenuItems.forEach(item => {
        item.style.display = 'none';
      });

      itemsToShow.forEach(item => {
        item.style.display = '';

        // For 2-tier menus, force all dropdowns to be visible (expanded mega menu style)
        if (isTwoTier && item.tagName.toLowerCase() === 'hover-disclosure') {
          item.classList.add('is-visible');
          item.setAttribute('aria-expanded', 'true');
          const dropdown = item.querySelector('.header__dropdown');
          if (dropdown) {
            dropdown.classList.add('is-visible');
          }
        }
      });

      // Check if there's only one parent item visible for this menu
      if (isTwoTier) {
        const visibleParents = itemsToShow.filter(item =>
          item.classList.contains('parent') && item.style.display !== 'none'
        );
        if (visibleParents.length === 1) {
          this.menuWrapper.setAttribute('data-single-parent', 'true');
        } else {
          this.menuWrapper.removeAttribute('data-single-parent');
        }
      } else {
        this.menuWrapper.removeAttribute('data-single-parent');
      }

      this.menuWrapper.style.display = '';
      this.menuWrapper.style.opacity = '1';
      this.menuWrapper.style.visibility = 'visible';
      this.menuWrapper.style.pointerEvents = 'all';

      // Restore the default menu spacing
      document.documentElement.style.setProperty('--default-menu-spacing', '50px');

      // Update overlay based on menu and third-tier state
      if (!this.isMenuOpen) {
        this.isMenuOpen = true;
      }

      const shouldLockScroll = topLevelNumber !== 1 || this.isThirdTierOpen;
      this.updateScrollLock(shouldLockScroll);
      this.updateOverlay();

      // Re-enable hover-disclosure elements after mouse has had time to settle
      // For 2-tier menus, disable hover-disclosure pointer events permanently
      setTimeout(() => {
        this.menuJustOpened = false;
        if (!isTwoTier) {
          this.hoverDisclosures.forEach(disclosure => {
            disclosure.style.pointerEvents = '';
          });
        } else {
          // For 2-tier menus, completely disable hover functionality
          // and keep forcing is-visible class
          this.hoverDisclosures.forEach(disclosure => {
            const group = parseInt(disclosure.getAttribute('data-top-level-group'));
            if (group === topLevelNumber) {
              disclosure.style.pointerEvents = 'none';
              // Force and lock the is-visible class
              disclosure.classList.add('is-visible');
              disclosure.setAttribute('aria-expanded', 'true');
              const dropdown = disclosure.querySelector('.header__dropdown');
              if (dropdown) {
                dropdown.classList.add('is-visible');
              }
              // But keep links clickable
              const links = disclosure.querySelectorAll('a');
              links.forEach(link => {
                link.style.pointerEvents = 'all';
              });
            }
          });

          // Set up a MutationObserver to prevent the theme from removing is-visible
          this.maintainTwoTierVisibility(topLevelNumber);
        }
      }, 150);
    }
  }

  updateScrollLock(shouldLock) {
    const hasScrollLock = document.documentElement.hasAttribute('data-scroll-locked');

    if (shouldLock && !hasScrollLock) {
      document.dispatchEvent(new CustomEvent('theme:scroll:lock', { bubbles: true }));
    } else if (!shouldLock && hasScrollLock) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }
  }

  ensureScrollUnlocked() {
    if (document.documentElement.hasAttribute('data-scroll-locked')) {
      document.dispatchEvent(new CustomEvent('theme:scroll:unlock', { bubbles: true }));
    }
  }

  setupWrappingDetection() {
    if (!this.menuWrapper) return;

    // Detect if menu items are wrapping to multiple lines
    const checkWrapping = () => {
      if (!this.menuWrapper) return;

      // Only check if menu is visible
      if (this.menuWrapper.style.opacity !== '1') return;

      const visibleItems = Array.from(this.menuWrapper.children).filter(
        item => item.style.display !== 'none' && item.getAttribute('data-nav-item')
      );

      if (visibleItems.length === 0) return;

      // Check if items are wrapping by comparing top positions
      const firstItemTop = visibleItems[0]?.getBoundingClientRect().top;
      const isWrapping = visibleItems.some(item => {
        const itemTop = item.getBoundingClientRect().top;
        return Math.abs(itemTop - firstItemTop) > 5; // 5px tolerance
      });

      // Add or remove wrapping attribute
      if (isWrapping) {
        this.menuWrapper.setAttribute('data-is-wrapping', 'true');
        console.log('Menu is wrapping - applying flex-start');
      } else {
        this.menuWrapper.removeAttribute('data-is-wrapping');
        console.log('Menu is NOT wrapping - using setting alignment');
      }
    };

    // Use ResizeObserver for better performance
    this.resizeObserver = new ResizeObserver(() => {
      requestAnimationFrame(checkWrapping);
    });

    this.resizeObserver.observe(this.menuWrapper);

    // Also check on menu open
    const originalShowMenu = this.showMenu.bind(this);
    this.showMenu = function(...args) {
      originalShowMenu(...args);
      setTimeout(checkWrapping, 100);
    };
  }

  maintainTwoTierVisibility(topLevelNumber) {
    // Disconnect any existing observer
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect();
    }

    // Create observer to force dropdowns to stay visible
    this.visibilityObserver = new MutationObserver(() => {
      const isTwoTier = this.menuTiersByTopLevel[topLevelNumber] === 2;
      if (isTwoTier && this.menuWrapper.getAttribute('data-two-tier') === 'true') {
        this.hoverDisclosures.forEach(disclosure => {
          const group = parseInt(disclosure.getAttribute('data-top-level-group'));
          if (group === topLevelNumber && !disclosure.classList.contains('is-visible')) {
            disclosure.classList.add('is-visible');
            disclosure.setAttribute('aria-expanded', 'true');
            const dropdown = disclosure.querySelector('.header__dropdown');
            if (dropdown && !dropdown.classList.contains('is-visible')) {
              dropdown.classList.add('is-visible');
            }
          }
        });
      }
    });

    // Observe all hover-disclosure elements
    this.hoverDisclosures.forEach(disclosure => {
      this.visibilityObserver.observe(disclosure, {
        attributes: true,
        attributeFilter: ['class', 'aria-expanded']
      });
    });
  }

  hideMenu() {
    setTimeout(() => {
      // Check if any top-level item is being hovered
      const anyTopLevelItemHovered = Array.from(this.topLevelItems).some(item => item.matches(':hover'));

      // Don't do anything if still hovering a top-level item or the menu
      if (anyTopLevelItemHovered || this.menuWrapper?.matches(':hover')) {
        return;
      }

      if (!this.menuWrapper?.matches(':hover') && !anyTopLevelItemHovered) {
        // If we're currently showing SHOP menu, just close any open third-tier dropdowns
        // but keep the SHOP menu visible
        if (this.currentHoveredItem === 1) {
          // Close any open third-tier dropdowns in SHOP menu
          this.hoverDisclosures.forEach(disclosure => {
            const group = parseInt(disclosure.getAttribute('data-top-level-group'));
            if (group === 1 && disclosure.classList.contains('is-visible')) {
              disclosure.classList.remove('is-visible');
              disclosure.setAttribute('aria-expanded', 'false');
              const dropdown = disclosure.querySelector('.header__dropdown');
              if (dropdown) {
                dropdown.classList.remove('is-visible');
              }
            }
          });

          // Reset third-tier state
          this.isThirdTierOpen = false;
          this.ensureScrollUnlocked();
          this.updateOverlay();
          return;
        }

        if (this.menuWrapper) {
          // Disconnect observer when hiding menu
          if (this.visibilityObserver) {
            this.visibilityObserver.disconnect();
          }

          // Don't actually hide the menu, just transition back to SHOP instantly
          // Reset third-tier state
          this.isThirdTierOpen = false;

          // Hide overlay
          this.updateOverlay();

          // Close all open dropdowns
          this.hoverDisclosures.forEach(disclosure => {
            if (disclosure.classList.contains('is-visible')) {
              disclosure.classList.remove('is-visible');
              disclosure.setAttribute('aria-expanded', 'false');
              const dropdown = disclosure.querySelector('.header__dropdown');
              if (dropdown) {
                dropdown.classList.remove('is-visible');
              }
            }
          });

          this.ensureScrollUnlocked();

          // Return to SHOP menu immediately
          this.showMenu(1);
          this.currentHoveredItem = 1;
        }
      }
    }, 100);
  }
}

// Initialize when DOM is ready
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    new TopLevelNavigation();
  });
} else {
  new TopLevelNavigation();
}

// Re-initialize on Shopify section load (for theme editor)
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    new TopLevelNavigation();
  });
}
